import { appLocale, isRussian } from "../../../locales/display";
import { msg } from "../../../locales/translate";
import type { ClientFinance } from "src/services/clientListData";
import { issueLevels } from "src/view/statView/overviewIssuePresentation";
import { accountingPalette } from "src/view/statView/accountingPalette";

const activityTimestamp = (value?: string | null): number => value
	? new Date(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value) ? `${value.replace(" ", "T")}Z` : value).getTime() : NaN;

export const formatClientActivity = (value?: string | null, now = Date.now()): string => {
	const timestamp = activityTimestamp(value);
	if (!Number.isFinite(timestamp)) return "—";
	const seconds = Math.max(0, (now - timestamp) / 1000);
	const units = [[31536000, "year"], [2592000, "mnth"], [86400, "day"], [3600, "hour"], [60, "min"], [1, "sec"]] as const;
	const [size, unit] = units.find(([size]) => seconds >= size) ?? units[units.length - 1];
	const count = Math.floor(seconds / size);
	if (isRussian()) {
		const relativeUnits = { year: "year", mnth: "month", day: "day", hour: "hour", min: "minute", sec: "second" } as const;
		return new Intl.RelativeTimeFormat("ru", { numeric: "always" }).format(-count, relativeUnits[unit]);
	}
	return msg("templateAgo_1b150fce", { v0: count, v1: unit, v2: count !== 1 && ["year", "day", "hour"].includes(unit) ? "s" : "" });
};

export const getClientActivity = (finance?: ClientFinance, now = Date.now()): { label: string; inactive: boolean } => {
	const timestamp = activityTimestamp(finance?.traffic.last_activity_at);
	const silent = finance?.traffic.silent_days;
	const validSilent = typeof silent === "number" && Number.isFinite(silent) && silent >= 0 ? silent : undefined;
	const elapsedDays = (now - timestamp) / 86400000;
	const inactive = Number.isFinite(timestamp) ? elapsedDays > 2 : validSilent !== undefined && validSilent > 2;
	return { label: formatClientActivity(finance?.traffic.last_activity_at, now), inactive };
};

export const formatClientDate = (value?: string | number | null): string => {
	if (value == null || value === "") return "—";
	const date = typeof value === "number" ? new Date(value < 1e12 ? value * 1000 : value)
		: new Date(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value) ? `${value.replace(" ", "T")}Z` : value);
	return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString(appLocale("en-GB"), { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
};

export const formatClientAttempts = (finance?: ClientFinance): string =>
	finance ? finance.traffic.attempts.toLocaleString(appLocale("en-US")) : "—";

export const formatClientConversion = (finance?: ClientFinance): string => {
	const value = finance?.traffic.conversion;
	return value == null || !Number.isFinite(value) ? "—" : `${(value * 100).toLocaleString(appLocale("en-US"), { maximumFractionDigits: 0 })}%`;
};

/** Conversion ratios use inclusive thresholds at 60%, 80% and 90%. */
export const getClientConversionColor = (value?: number | null): string => {
	if (value == null || !Number.isFinite(value)) return issueLevels.info.color;
	if (value < 0.6) return issueLevels.critical.color;
	if (value < 0.8) return issueLevels.warning.color;
	if (value < 0.9) return accountingPalette.released.foreground;
	return accountingPalette.positive.foreground;
};

export const formatClientMoney = (finance: ClientFinance | undefined, metric: "turnover" | "profit"): string => {
	if (!finance) return "—";
	const amounts = Object.entries(finance.money);
	// An empty currency map provides no amount; it is not a reported zero balance.
	return amounts.length ? amounts.map(([currency, money]) =>
		`${money[metric].toLocaleString(appLocale("en-US"), { minimumFractionDigits: 2, maximumFractionDigits: metric === "profit" ? 4 : 2 })} ${currency}`,
	).join(" · ") : "—";
};
